import { useState, createContext, useContext, useEffect } from 'react';
import '@/App.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Phone, Mail, MapPin, ShoppingBag, Trash2, Send, CheckCircle, 
  Paintbrush, Wrench, Pipette, Droplets, Lightbulb, Zap, Drill, Sparkles,
  Shield, Package, ChevronRight, Star, Building2, Clock, Users, Hammer, ShieldCheck
} from 'lucide-react';

import { categories, brands } from './productsData';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import brandlogo from './assets/images/advaya-industrialhome-brandlogo.webp';
import industrypaint from './assets/images/industry-paint.webp';
import paintingtool from './assets/images/painting-safety-tool.webp';
import carouselimage from './assets/images/carousel-image.webp';
import carouselimage2 from './assets/images/carousel-image-2.webp';
import lighting from './assets/images/lighting.webp';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InquiryContext = createContext();

const useInquiry = () => {
  const context = useContext(InquiryContext);
  if (!context) {
    throw new Error('useInquiry must be used within InquiryProvider');
  }
  return context;
};

const InquiryProvider = ({ children }) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const addProduct = (product, category) => {
    const productWithCategory = `${category} - ${product}`;
    if (!selectedProducts.includes(productWithCategory)) {
      setSelectedProducts([...selectedProducts, productWithCategory]);
      prev => [...prev, productWithCategory]
    }
    setIsDrawerOpen(true); // Always open drawer when adding
  };

  const removeProduct = (product) => {
    setSelectedProducts(selectedProducts.filter(p => p !== product));
  };

  const clearProducts = () => {
    setSelectedProducts([]);
  };

  return (
    <InquiryContext.Provider value={{ selectedProducts, addProduct, removeProduct, clearProducts, isDrawerOpen, setIsDrawerOpen }}>
      {children}
    </InquiryContext.Provider>
  );
};

const categoryIcons = {
  'surface-coating-decorative': Paintbrush,
  'surface-coating-protective': ShieldCheck,
  'hardware-sanitary': Wrench,
  'pipes-fittings': Pipette,
  'abro-products': Droplets,
  'lighting': Lightbulb,
  'electricals': Zap,
  'sanders-polishers': Drill,
  'spray-guns': Sparkles,
  'air-tools': Hammer,
  'abrasives-consumables': Package,
  'safety-maintenance': Shield,
};
const carouselImages = [
  industrypaint,
  carouselimage2,
  carouselimage,
  lighting
];
const Navigation = () => {
  
  const [isOpen, setIsOpen] = useState(false);
  const { selectedProducts, setIsDrawerOpen } = useInquiry();

  const navItems = [
    { label: 'Home', href: '#home' },
    { label: 'Brands', href: '#brands' },
    { label: 'Products', href: '#products' },
    { label: 'Contact', href: '#contact' }
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 flex items-center justify-center shadow-lg">
              {/* <span className="text-white font-black text-2xl">A</span> */}
              <img className="object-cover w-full h-full rounded-xl" src={brandlogo} alt='Advaya Industrial Supply and Solutions logo Indore' loading="lazy" decoding="async"></img>
            </div>
            <div>
              <h1 className="logo-title text-2xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text !text-transparent inline-block">
ADVAYA</h1>
              <p className="text-xs text-slate-700 font-medium tracking-wide logo-subtitle">Industrial Supply & Solutions</p>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-sm font-medium text-slate-600 hover:text-orange-500 transition-colors relative group"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="relative bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-6 py-2.5 rounded-full hover:shadow-lg hover:shadow-orange-200 hover:-translate-y-0.5 transition-all font-semibold text-sm flex items-center gap-2"
              data-testid="inquiry-cart-button"
            >
              <ShoppingBag className="w-4 h-4" />
              Inquiry
              {selectedProducts.length > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                  {selectedProducts.length}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-white border-t overflow-hidden"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="block py-3 px-4 rounded-xl text-slate-700 hover:bg-orange-50 hover:text-orange-600 font-medium transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setIsDrawerOpen(true);
                  setIsOpen(false);
                }}
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-4 h-4" />
                Inquiry ({selectedProducts.length})
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const HeroSection = () => {
  const [currentImage, setCurrentImage] = useState(0);

// useEffect(() => {
//   const interval = setInterval(() => {
//     setCurrentImage((prev) => (prev + 1) % carouselImages.length);
//   }, 3000); // 3 sec

//   return () => clearInterval(interval);
// }, []);
useEffect(() => {
  const interval = setInterval(() => {
    document.visibilityState === "visible" &&
      setCurrentImage(prev => (prev + 1) % carouselImages.length);
  }, 3000);

  return () => clearInterval(interval);
}, []);
  return (
    <section id="home" className="relative min-h-[85vh] bg-gradient-to-br from-slate-50 via-orange-50/30 to-yellow-50/30 overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-yellow-200 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-8 pb-0 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-slate-600">Trusted by multiple businesses in Indore</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight">
              Your One-Stop
              <span className="block bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
                Industrial Solution
              </span>
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
              Authorized dealer for Asian Paints, Birla Opus, Parryware and more premium brands.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#productscategories"
                className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:shadow-orange-200 hover:-translate-y-1 transition-all font-semibold flex items-center gap-2"
                data-testid="explore-products-button"
              >
                Explore Range
                <ChevronRight className="w-5 h-5" />
              </a>
              <a
                href="#contact"
                className="bg-white text-slate-700 px-8 py-4 rounded-xl hover:shadow-lg hover:-translate-y-1 transition-all font-semibold border border-slate-200"
                data-testid="contact-us-button"
              >
                Contact Us
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">1000+</p>
                  <p className="text-sm text-slate-700">Products</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">10+</p>
                  <p className="text-sm text-slate-700">Brands</p>
                </div>
              </div>
              {/* <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">10+</p>
                  <p className="text-sm text-slate-700">Years</p>
                </div>
              </div> */}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
              {/* <img
                src="https://images.unsplash.com/photo-1631856956423-2b95dae0ba74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwc2hlbHZlcyUyMHN0b2NrZWR8ZW58MHx8fHwxNzczOTMwOTYwfDA&ixlib=rb-4.1.0&q=85"
                alt="ADVAYA Industrial Warehouse"
                className="w-full h-[400px] lg:h-[450px] object-cover"
              /> */}
              <div className="relative w-full h-[400px] lg:h-[450px] overflow-hidden">
  <AnimatePresence mode="wait">
    <motion.img
      key={currentImage}
      src={carouselImages[currentImage]}
      alt="Industrial paint and coating supplies warehouse Indore"
      loading={currentImage === 0 ? "eager" : "lazy"}
  fetchpriority={currentImage === 0 ? "high" : "auto"}

      className="absolute w-full h-full object-cover"
      initial={{ opacity: 0, scale: 1.05 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8 }}
      
    />
  </AnimatePresence>
</div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Authorized Channel Partner</p>
                    <p className="text-sm text-slate-700">Asian Paints • Asian Paints PPG • Birla Opus • Parryware • Ganit Lighting </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const BrandsSection = () => {
  return (
    <section id="brands" className="py-8 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="inline-block px-4 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4">Authorized Dealer</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">Our Partner Brands</h2>
        </motion.div>

        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4 flex justify-center items-stretch">
          {brands.map((brand, index) => (
            <motion.div
              key={brand.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-slate-50 rounded-xl p-4 flex flex-col items-center justify-center hover:shadow-lg hover:-translate-y-1 transition-all group cursor-pointer"
              style={{ minHeight: '100px' }}
              data-testid={`brand-${brand.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: `${brand.color}20` }}
              >
                {/* <span 
                  className="text-xl font-bold"
                  style={{ color: brand.color }}
                >
                  {brand.name.charAt(0)}
                </span> */}
                <img 
  src={brand.logo} 
  alt={`${brand.name} industrial products supplier`}
  loading="lazy" 
  decoding="async"
  className="w-12 h-12 rounded-xl"
/>
              </div>
              <p className="text-xs font-semibold text-slate-700 text-center leading-tight">{brand.name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CategoryCard = ({ category, index }) => {
  const IconComponent = categoryIcons[category.id] || Package;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
      data-testid={`category-card-${category.id}`}
    >
      <div className="relative h-40 overflow-hidden">
        <img
          src={category.image}
          alt={`${category.name} industrial products supplier Indore`}
          loading="lazy" 
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className={`absolute top-3 left-3 w-10 h-10 ${category.bgColor} rounded-xl flex items-center justify-center shadow-lg`}>
          <IconComponent className="w-5 h-5 text-white" />
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          {/* <span className="text-white/80 text-xs font-medium">{category.products.length} Products</span> */}
          <h3 className="text-lg font-bold text-white leading-tight">{category.name}</h3>
        </div>
      </div>
      <div className="p-4">
        <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 group-hover:line-clamp-none transition-all duration-300">{category.description}</p>
        <a
          href="#products"
          className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors"
          onClick={() => {
            setTimeout(() => {
              document.getElementById(`tab-${category.id}`)?.click();
            }, 300);
          }}
        >
          Explore Range
          <ChevronRight className="w-4 h-4" />
        </a>
      </div>
    </motion.div>
  );
};

const ProductShowcase = () => {
  const [activeTab, setActiveTab] = useState(categories[0].id);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const { addProduct } = useInquiry();

  const activeCategory = categories.find(cat => cat.id === activeTab);
  const IconComponent = categoryIcons[activeTab] || Package;

  return (
    <section id="products" className="pb-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="inline-block px-4 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4">Our Inventory</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">Product Catalog</h2>
          <p className="text-slate-600 mt-2">Browse our product range and explore solutions across categories</p>
        </motion.div>

        {/* Category Tabs */}
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="flex gap-2 min-w-max">
            {categories.map((category) => {
              const TabIcon = categoryIcons[category.id] || Package;
              return (
                <button
                
                  key={category.id}
                  id={`tab-${category.id}`}
                  onClick={() => setActiveTab(category.id)}
                  className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center gap-2 whitespace-nowrap ${
                    activeTab === category.id
                      ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg shadow-orange-200'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                  data-testid={`product-tab-${category.id}`}
                >
                  <TabIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">{category.name}</span>
                  <span className="sm:hidden">{category.name.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Category Header */}
        <div className="flex items-center gap-4 mb-6 p-5 bg-white rounded-2xl shadow-sm">
          <div className={`w-12 h-12 ${activeCategory?.bgColor} rounded-xl flex items-center justify-center shadow-lg`}>
            <IconComponent className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-800">{activeCategory?.name}</h3>
            <p className="text-slate-700 text-sm">{activeCategory?.description}</p>
          </div>
        </div>

        {/* Products Grid with Images */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {activeCategory?.products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-orange-200 hover:shadow-lg transition-all group"
                data-testid={`product-card-${index}`}
              >
                {/* Product Image */}
                <div className="relative h-52 overflow-hidden bg-slate-100">
                  <img
                    src={product.image}
                    alt={`${product.name} - ${activeCategory.name} industrial product`}
                    loading="lazy" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 768px) 100vw, 33vw"
                    decoding="async"
  //                    className={`w-full h-full ${
  //   product.noObjectCover ? '' : 'object-cover'
  // } group-hover:scale-105 transition-transform duration-500`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
                
                {/* Product Info */}
                <div className="p-4">
                  <h4 className="font-semibold text-slate-800 group-hover:text-orange-600 transition-colors mb-1 line-clamp-1">
                    {product.name}
                  </h4>
                  {/* <p className="text-sm text-slate-700 mb-4 line-clamp-2">{product.description}</p> */}
                  <p
  className={`text-sm text-slate-600/700 ${
    expandedIndex === product.name ? '' : 'line-clamp-2'
  } ${
    product.description.split(' ').length > 12 ? '' : 'mb-4'
  }`}
> {product.description}</p>
                  {product.description.split(' ').length > 12 && <button
  onClick={() =>
    setExpandedIndex(expandedIndex === product.name ? null : product.name)
  }
  className="text-xs text-orange-500 mb-4"
>
  {expandedIndex === product.name ? 'Show less' : 'Read more'}
</button>}
                  <button
                    onClick={() => addProduct(product.name, activeCategory.name)}
                    className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2.5 rounded-lg hover:shadow-lg hover:shadow-orange-200 transition-all font-medium text-sm flex items-center justify-center gap-2"
                    data-testid={`add-to-inquiry-${index}`}
                  >
                    <ShoppingBag className="w-4 h-4" />
                    Add to Inquiry
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const InquiryDrawer = () => {
  
  const { selectedProducts, removeProduct, clearProducts, isDrawerOpen, setIsDrawerOpen } = useInquiry();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProducts.length === 0) {
      toast.error('Please add at least one product to inquiry');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/inquiry`, {
        ...formData,
        products: selectedProducts
      });

      if (response.data) {
        setIsSuccess(true);
        toast.success('Inquiry sent successfully! We will contact you soon.');
        
        setTimeout(() => {
          setFormData({ name: '', email: '', phone: '', company: '', message: '' });
          clearProducts();
          setIsSuccess(false);
          setIsDrawerOpen(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsDrawerOpen(false)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            data-testid="drawer-overlay"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full md:w-[450px] bg-white z-[60] overflow-y-auto shadow-2xl"
            data-testid="inquiry-drawer"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-yellow-500 p-6 z-10">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="w-6 h-6 text-white" />
                  <h2 className="text-xl font-bold text-white">Your Inquiry</h2>
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors text-white"
                  data-testid="close-drawer-button"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {isSuccess ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">Inquiry Sent!</h3>
                  <p className="text-slate-600">We'll contact you within 24 hours.</p>
                </motion.div>
              ) : (
                <>
                  {/* Selected Products */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-slate-800">Selected Products ({selectedProducts.length})</h3>
                      {selectedProducts.length > 0 && (
                        <button
                          onClick={clearProducts}
                          className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
                          data-testid="clear-all-products"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedProducts.length === 0 ? (
                        <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                          <ShoppingBag className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-400">No products selected yet</p>
                          <p className="text-xs text-slate-400 mt-1">Browse products and click "Add to Inquiry"</p>
                        </div>
                      ) : (
                        selectedProducts.map((product, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center p-3 bg-slate-50 rounded-xl"
                            data-testid={`selected-product-${index}`}
                          >
                            <span className="text-sm font-medium text-slate-700 flex-1 pr-2 line-clamp-1">{product}</span>
                            <button
                              onClick={() => removeProduct(product)}
                              className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                              data-testid={`remove-product-${index}`}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Your full name"
                        data-testid="inquiry-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="your@email.com"
                        data-testid="inquiry-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone *</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="inquiry-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Company (Optional)</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Company name"
                        data-testid="inquiry-company-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Message (Optional)</label>
                      <textarea
                        rows="3"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                        placeholder="Any specific requirements..."
                        data-testid="inquiry-message-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting || selectedProducts.length === 0}
                      className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-6 py-4 rounded-xl hover:shadow-lg hover:shadow-orange-200 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="submit-inquiry-button"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Inquiry
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const ContactSection = () => {
  return (
    <section id="contact" className="py-8 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="inline-block px-4 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4">Get In Touch</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">Contact Us</h2>
          <p className="text-slate-600 mt-2">We're here to help with all your industrial supply needs</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all text-center"
            data-testid="contact-phone"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-blue-200">
              <Phone className="text-white w-7 h-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-3">Phone</h3>
            <div className="space-y-1 text-slate-600">
              <a href="tel:+918770911933" className="block hover:text-orange-500 transition-colors">+91 8770911933</a>
              <a href="tel:+918085970820" className="block hover:text-orange-500 transition-colors">+91 8085970820</a>
              {/* <a href="tel:+917987698329" className="block hover:text-orange-500 transition-colors">+91 7987698329</a> */}
              <a href="tel:07314015796" className="block hover:text-orange-500 transition-colors">0731-4015796</a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all text-center"
            data-testid="contact-email"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-orange-200">
              <Mail className="text-white w-7 h-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-3">Email</h3>
            <a href="mailto:salesadvaya@gmail.com" className="text-slate-600 hover:text-orange-500 transition-colors">salesadvaya@gmail.com</a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all text-center"
            data-testid="contact-address"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-green-200">
              <MapPin className="text-white w-7 h-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-3">Address</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Industrial Supplier in Indore, Madhya Pradesh, India - 453331
              Shop No 1 & 2, Vrindavan Arcade,<br />
              Near HDFC Bank, Rau-Rangwasa Road,<br />
              Indore-453331 (M.P)
            </p>
             <a
    href="https://www.google.com/maps/dir/?api=1&destination=ADVAYA+INDUSTRIAL+SUPPLY+AND+SOLUTIONS,+Rau,+Indore"
    target="_blank"
    rel="noopener noreferrer"
    className="inline-block mt-4 text-orange-500 font-semibold hover:underline"
  >
    Get Directions →
  </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                              <img className="object-cover w-full h-full rounded-xl" src={brandlogo} alt='Advaya Industrial Supply and Solutions logo Indore' loading="lazy" decoding="async" ></img>

                {/* <span className="text-white font-black text-xl">A</span> */}
              </div>
              <div>
                <h3 className="text-xl font-bold">ADVAYA</h3>
                <p className="text-xs text-slate-400">Industrial Supply & Solutions LLP</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              Your One-Stop Industrial Solution - Your trusted partner for all industrial supply and material requirements in Indore.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Authorized Dealer</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>• Asian Paints</li>
              <li>• Birla Opus</li>
              <li>• Asian Paints PPG</li>
              <li>• Parryware</li>
              <li>• Ganit</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#home" className="text-slate-400 hover:text-orange-400 transition-colors">Home</a></li>
              <li><a href="#brands" className="text-slate-400 hover:text-orange-400 transition-colors">Brands</a></li>
              <li><a href="#products" className="text-slate-400 hover:text-orange-400 transition-colors">Products</a></li>
              <li><a href="#contact" className="text-slate-400 hover:text-orange-400 transition-colors">Contact</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 text-center">
          <p className="text-sm text-slate-700">
            © 2026 ADVAYA Industrial Supply & Solutions LLP. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <InquiryProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <Navigation />
        <HeroSection />
        <BrandsSection />
        
        {/* Categories Section */}
        <section id="categories" className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <span className="inline-block px-4 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4">What We Offer</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800"  id="productscategories">Our Product Range</h2>
              <p className="text-slate-600 mt-2">Explore our wide range of industrial solutions</p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {categories.map((category, index) => (
                <CategoryCard key={category.id} category={category} index={index} />
              ))}
            </div>
          </div>
        </section>
        <ProductShowcase />
        <section className="py-10 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    
    <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4">
      Industrial Paint & Hardware Supplier in Indore
    </h2>

    <p className="text-slate-600 max-w-3xl mx-auto leading-relaxed">
      Advaya Industrial Supply & Solutions is a up-and-coming supplier of industrial paints,
      coatings, hardware, and electrical materials in Indore, Madhya Pradesh. We are
      authorized dealers of Asian Paints, Birla Opus, and Parryware, providing high-quality
      solutions for residential, commercial, and industrial projects.
    </p>

  </div>
</section>
        <ContactSection />
        <Footer />
        <InquiryDrawer />
      </div>
    </InquiryProvider>
  );
}

export default App;
